import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSession } from '../services/api';
import apiClient from '../services/apiClient';
import { toast } from 'react-toastify';

export const MagicLoginRedirect: React.FC<{ onLogin?: (user: any) => void }> = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState<'init' | 'verifying' | 'checking' | 'done'>('init');

  useEffect(() => {
    let isMounted = true; // Track if component is still mounted

    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (!token) {
      if (isMounted) {
        setError('No token found');
        setLoading(false);
      }
      return;
    }

    const doVerification = async () => {
      try {
        if (!isMounted) return;
        setStage('verifying');
        console.log('Verifying token...');
        
        // Token verifizieren - Backend setzt Cookie und macht 302 Redirect
        // Wir müssen den Request machen, aber Redirects/Fehler ignorieren
        try {
          const response = await apiClient.get(`/api/v2/web/auth/magic-link/verify?token=${token}`, {
            maxRedirects: 0, // Folge keinen Redirects
            validateStatus: () => true, // Akzeptiere alle Status-Codes
            headers: {
              'X-Skip-Interceptor': 'true' // Custom header to skip error interceptors
            }
          });
          console.log('Magic link verification response:', response.status);
        } catch (redirectError: any) {
          // 302 Redirect oder Network Error ist OK - Cookie wurde gesetzt
          console.log('Magic link request completed (302 expected):', redirectError.message);
        }
        
        // Kurze Pause, um dem Browser Zeit zu geben, Cookies zu verarbeiten
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (!isMounted) return;
        setStage('checking');
        console.log('Checking session status...');
        
        // Session-Status abfragen
        const sessionData = await getSession();
        console.log('Session data received:', sessionData);
        
        if (!isMounted) return;
        
        if (sessionData.authenticated && sessionData.user) {
          // Session wurde erfolgreich erstellt und Benutzer ist authentifiziert
          console.log('Authentication successful, user:', sessionData.user);
          
          if (onLogin) {
            onLogin(sessionData.user);
          }
          
          // Success notification with Toast (nur einmal anzeigen)
          const userName = sessionData.user.firstname || sessionData.user.mail || 'User';
          toast.success(`🎉 Welcome back, ${userName}!`, {
            position: "top-center",
            autoClose: 3000,
            toastId: 'magic-login-success', // Verhindert Duplikate
          });
          
          if (!isMounted) return;
          setStage('done');
          
          // Zum gewünschten Ziel navigieren
          let targetUrl = '/events'; // Default
          
          try {
            // Base64-decodierten Token-Payload parsen (erster Teil des Tokens)
            const tokenParts = token.split('.');
            const decodedData = atob(tokenParts[0]);
            const tokenData = JSON.parse(decodedData);
            const forward_url = tokenData.forward_url;
            
            // Nur verwenden, wenn es ein spezifisches Ziel ist (nicht "/" oder leer)
            if (forward_url && forward_url !== '/' && forward_url !== '') {
              targetUrl = forward_url;
            }
            
            console.log('Token forward_url:', forward_url, '→ Using:', targetUrl);
          } catch (e) {
            console.error('Error parsing token, using default /events:', e);
          }
          
          console.log('Redirecting to:', targetUrl);
          
          // Kurze Verzögerung, damit Toast sichtbar ist, dann Hard Redirect
          // Hard Redirect lädt die komplette App neu und aktualisiert Rollen/Navbar
          setTimeout(() => {
            console.log('Executing hard redirect to:', targetUrl);
            window.location.href = targetUrl;
          }, 1000);
        } else {
          throw new Error('Authentication failed');
        }
      } catch (err: any) {
        if (!isMounted) return;
        console.error('Magic link error:', err);
        
        // Granulare Fehlerbehandlung
        let errorMessage = 'Unknown error occurred';
        
        if (err.response) {
          // Server hat mit einem Fehlercode geantwortet
          const status = err.response.status;
          const data = err.response.data;
          
          if (status === 401 || status === 403) {
            errorMessage = 'Invalid or expired magic link. Please request a new one.';
          } else if (status === 404) {
            errorMessage = 'Authentication endpoint not found. Please contact support.';
          } else if (status === 500) {
            errorMessage = data?.detail || 'Server error occurred. Please try again later.';
          } else if (data?.detail) {
            errorMessage = data.detail;
          } else {
            errorMessage = `Server error (${status}). Please try again.`;
          }
        } else if (err.request) {
          // Request wurde gesendet, aber keine Antwort erhalten
          errorMessage = 'No response from server. Please check your internet connection.';
        } else if (err.message) {
          // Fehler beim Setup des Requests
          if (err.message.includes('Network Error')) {
            errorMessage = 'Network error. Please check your internet connection.';
          } else if (err.message.includes('timeout')) {
            errorMessage = 'Request timeout. Please try again.';
          } else {
            errorMessage = err.message;
          }
        }
        
        setError(errorMessage);
        setLoading(false);
        
        // Fehler auch als Toast anzeigen (nur einmal)
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
          toastId: 'magic-login-error', // Verhindert Duplikate
        });
      }
    };

    doVerification();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Nur einmal beim Mount ausführen

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 5rem)',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{
          background: '#ffffff',
          padding: '3rem',
          borderRadius: '10px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          width: '100%'
        }}>
          <div style={{
            fontSize: '3rem',
            marginBottom: '1.5rem'
          }}>
            {stage === 'init' && '🔐'}
            {stage === 'verifying' && '🔍'}
            {stage === 'checking' && '✅'}
            {stage === 'done' && '🎉'}
          </div>
          <h2 style={{ 
            marginBottom: '1rem',
            color: '#333',
            fontSize: '1.5rem'
          }}>
            Authentication in progress...
          </h2>
          <p style={{
            color: '#666',
            fontSize: '1.1rem',
            marginBottom: '1.5rem'
          }}>
            {stage === 'init' && 'Initializing...'}
            {stage === 'verifying' && 'Verifying your magic link...'}
            {stage === 'checking' && 'Checking your login status...'}
            {stage === 'done' && 'Redirecting you now...'}
          </p>
          <div style={{
            display: 'inline-block',
            width: '50px',
            height: '50px',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 5rem)',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{
          background: '#ffffff',
          padding: '3rem',
          borderRadius: '10px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          width: '100%'
        }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: '1.5rem'
          }}>
            ❌
          </div>
          <h2 style={{ 
            marginBottom: '1rem',
            color: '#e74c3c',
            fontSize: '1.5rem'
          }}>
            Login Error
          </h2>
          <p style={{
            color: '#666',
            fontSize: '1rem',
            marginBottom: '2rem',
            padding: '1rem',
            background: '#fee',
            borderRadius: '5px',
            border: '1px solid #fcc'
          }}>
            {error}
          </p>
          <button 
            onClick={() => navigate('/login', { replace: true })}
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              transition: 'background-color 0.3s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2980b9'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3498db'}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 5rem)',
      fontSize: '1.2rem',
      color: '#666'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚀</div>
        Redirecting...
      </div>
    </div>
  );
};
